import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useGlobalSettingStore } from 'src/models/useGlobalSettingModel';

import PageNotFound from '../../../../components/Common/NoPermission';
import { useLoginUserStore } from '../../../../models/useLoginUserModel';
import {
  getAtpPolicyConfig,
  getExchangeTemplateConfig,
} from '../../TemplateConfig/cfg.exchange';
import ExchangeOnlinePolicy from '../PolicyTemplate/Email/ExchangeOnline';
import {
  EnableDisableEnum,
  ExchangeOnlinePolicyItem,
} from '@Policy/utils/types';
import ExchangeOnlineInbound from '@Policy/AdvancedThreatProtection/PolicyTemplate/Email/ExchangeOnlineInbound';
import ExchangeOnlineOutbound from '@Policy/AdvancedThreatProtection/PolicyTemplate/Email/ExchangeOnlineOutbound';
import GmailAtpPolicy from '@Policy/AdvancedThreatProtection/PolicyTemplate/Email/Gmail';
import GmailInlineInbound from '@Policy/AdvancedThreatProtection/PolicyTemplate/Email/GmailInlineInbound';
import GmailInlineOutbound from '@Policy/AdvancedThreatProtection/PolicyTemplate/Email/GmailInlineOutbound';
import OneDrive from '@Policy/AdvancedThreatProtection/PolicyTemplate/Collaboration/OneDrive';
import SharePoint from '@Policy/AdvancedThreatProtection/PolicyTemplate/Collaboration/SharePoint';
import Teams from '@Policy/AdvancedThreatProtection/PolicyTemplate/Collaboration/Teams';
import TeamsChat from '@Policy/AdvancedThreatProtection/PolicyTemplate/Collaboration/TeamsChat';
import Box from '@Policy/AdvancedThreatProtection/PolicyTemplate/Collaboration/Box';
import Dropbox from '@Policy/AdvancedThreatProtection/PolicyTemplate/Collaboration/Dropbox';
import GoogleDrive from '@Policy/AdvancedThreatProtection/PolicyTemplate/Collaboration/GoogleDrive';
import SalesforceSandbox from '@Policy/AdvancedThreatProtection/PolicyTemplate/Collaboration/SalesforceSandbox';
import SalesforceProduction from '@Policy/AdvancedThreatProtection/PolicyTemplate/Collaboration/SalesforceProduction';

import { useOrgStore } from 'src/models/useOrgModel';
import { useAccountStore } from 'src/models/useAccountModel';
import { getExchangeInboundTemplateConfig } from '../../TemplateConfig/cfg.exchangeInbound';
import {
  CollaborationPolicyNameEnum,
  MailPolicyNameEnum,
} from '../../utils/types';
import { getExchangeOutboundTemplateConfig } from '../../TemplateConfig/cfg.exchangeOutbound';
import { getGmailTemplateConfig } from '../../TemplateConfig/cfg.gmail';
import { getOneDriveTemplateConfig } from '../../TemplateConfig/cfg.oneDrive';
import { getGmailInboundTemplateConfig } from '../../TemplateConfig/cfg.gmailInbound';
import { getGmailOutboundTemplateConfig } from '../../TemplateConfig/cfg.gmailOutbound';
import { getTeamsTemplateConfig } from '../../TemplateConfig/cfg.teams';
import { getTeamsChatTemplateConfig } from '../../TemplateConfig/cfg.teamsChat';
import { getSharePointTemplateConfig } from '../../TemplateConfig/cfg.sharePoint';
import { getBoxTemplateConfig } from '@Policy/TemplateConfig/cfg.box';
import { getGoogleDriveTemplateConfig } from '@Policy/TemplateConfig/cfg.googledrive';
import { getDropboxTemplateConfig } from '@Policy/TemplateConfig/cfg.dropbox';
import { getSalesforceSandboxTemplateConfig } from '@Policy/TemplateConfig/cfg.salesforceSandbox';
import { getSalesforceProductionTemplateConfig } from '@Policy/TemplateConfig/cfg.salesforceProduction';

const Policies = () => {
  const { service } = useParams();
  const { t } = useTranslation();
  const loginUserModel = useLoginUserStore();
  const globalSettingModule = useGlobalSettingStore();
  const { orgId, switchOrgId } = useOrgStore();
  const { groupCompanyGuid } = useAccountStore();
  const { selectedOrgGlobalSetting } = globalSettingModule;

  const policyComponent = useMemo(() => {
    let templateData: any = {};
    let configData: any = {};
    let newOrgId = orgId;

    if (orgId === 'all') {
      switchOrgId(groupCompanyGuid);
      newOrgId = groupCompanyGuid;
    }

    switch (service) {
      case MailPolicyNameEnum.Exchange:
        templateData = getExchangeTemplateConfig(
          t,
          selectedOrgGlobalSetting,
          loginUserModel.user?.accountEmail
        ) as ExchangeOnlinePolicyItem;
        // Enable risk ai email banners for new policies only
        templateData.tmRiskAiFilters.raiAnomalyAddBannerEnable =
          EnableDisableEnum.enabled;
        templateData.tmRiskAiFilters.raiSecurityRisksAddBannerEnabled =
          EnableDisableEnum.enabled;
        configData = getAtpPolicyConfig(
          t,
          selectedOrgGlobalSetting,
          templateData
        );
        return (
          <ExchangeOnlinePolicy
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
      case MailPolicyNameEnum.ExchangeInlineInbound:
        templateData = getExchangeInboundTemplateConfig(
          t,
          selectedOrgGlobalSetting,
          loginUserModel.user?.accountEmail
        );
        configData = getAtpPolicyConfig(
          t,
          selectedOrgGlobalSetting,
          templateData
        );
        return (
          <ExchangeOnlineInbound
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
      case MailPolicyNameEnum.ExchangeInlineOutbound:
        templateData = getExchangeOutboundTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getAtpPolicyConfig(
          t,
          selectedOrgGlobalSetting,
          templateData
        );
        return (
          <ExchangeOnlineOutbound
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
      case MailPolicyNameEnum.Gmail:
        templateData = getGmailTemplateConfig(
          t,
          selectedOrgGlobalSetting,
          loginUserModel.user?.accountEmail
        );
        configData = getAtpPolicyConfig(
          t,
          selectedOrgGlobalSetting,
          templateData
        );
        return (
          <GmailAtpPolicy
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
      case MailPolicyNameEnum.GmailInlineInbound:
        templateData = getGmailInboundTemplateConfig(
          t,
          selectedOrgGlobalSetting,
          loginUserModel.user?.accountEmail
        );
        configData = getAtpPolicyConfig(
          t,
          selectedOrgGlobalSetting,
          templateData
        );
        return (
          <GmailInlineInbound
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
      case MailPolicyNameEnum.GmailInlineOutbound:
        templateData = getGmailOutboundTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getAtpPolicyConfig(
          t,
          selectedOrgGlobalSetting,
          templateData
        );
        return (
          <GmailInlineOutbound
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
      case CollaborationPolicyNameEnum.OneDrive:
        templateData = getOneDriveTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getAtpPolicyConfig(
          t,
          selectedOrgGlobalSetting,
          templateData
        );
        return (
          <OneDrive
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
      case CollaborationPolicyNameEnum.SharePoint:
        templateData = getSharePointTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getAtpPolicyConfig(
          t,
          selectedOrgGlobalSetting,
          templateData
        );
        return (
          <SharePoint
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
      case CollaborationPolicyNameEnum.Teams:
        templateData = getTeamsTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getAtpPolicyConfig(
          t,
          selectedOrgGlobalSetting,
          templateData
        );
        return (
          <Teams template={templateData} config={configData} orgId={newOrgId} />
        );
      case CollaborationPolicyNameEnum.TeamsChat:
        templateData = getTeamsChatTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getAtpPolicyConfig(
          t,
          selectedOrgGlobalSetting,
          templateData
        );
        return (
          <TeamsChat
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
      case CollaborationPolicyNameEnum.Box:
        templateData = getBoxTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getAtpPolicyConfig(
          t,
          selectedOrgGlobalSetting,
          templateData
        );
        return (
          <Box template={templateData} config={configData} orgId={newOrgId} />
        );
      case CollaborationPolicyNameEnum.GoogleDrive:
        templateData = getGoogleDriveTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getAtpPolicyConfig(
          t,
          selectedOrgGlobalSetting,
          templateData
        );
        return (
          <GoogleDrive
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
      case CollaborationPolicyNameEnum.Dropbox:
        templateData = getDropboxTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getAtpPolicyConfig(
          t,
          selectedOrgGlobalSetting,
          templateData
        );
        return (
          <Dropbox
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
      case CollaborationPolicyNameEnum.SalesforceSandbox:
        templateData = getSalesforceSandboxTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getAtpPolicyConfig(
          t,
          selectedOrgGlobalSetting,
          templateData
        );
        return (
          <SalesforceSandbox
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
      case CollaborationPolicyNameEnum.SalesforceProduction:
        templateData = getSalesforceProductionTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getAtpPolicyConfig(
          t,
          selectedOrgGlobalSetting,
          templateData
        );
        return (
          <SalesforceProduction
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
      default:
        return <PageNotFound />;
    }
  }, [
    groupCompanyGuid,
    loginUserModel.user?.accountEmail,
    orgId,
    selectedOrgGlobalSetting,
    service,
    switchOrgId,
    t,
  ]);

  return <>{policyComponent}</>;
};
export default Policies;
